import { faFileAlt } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";

import { setSetting } from "actions";

import { MultiButtonOption } from "components/Button/MultiButton/types";

import { DeploymentPermissions } from "constants/permissions";
import { Settings } from "constants/settings";

import { openAttachments } from "lib/navigation";

import {
  getCurrentAgenciesOptions,
  getIsSaleyardAdmin,
  getSetting,
} from "selectors";

import { useHasDeploymentPermission } from "hooks/useHasPermission";
import { useHasSelectedMobileView } from "hooks/useScreenSize";

interface AgencyOption {
  label: string;
  value: number;
}

const TITLE = "View Attachments";
const DATA_TOUR = "viewAttachments";

export function useViewAttachmentsAction(
  overrides: Partial<MultiButtonOption> = {},
): MultiButtonOption {
  const dispatch = useDispatch();
  const isSaleyardAdmin = useSelector(getIsSaleyardAdmin);
  const agencyId = useSelector(getSetting(Settings.agencyId)) as number | null;
  const agencyOptions = useSelector(
    getCurrentAgenciesOptions(false),
  ) as AgencyOption[];
  const hasCreateConsignmentPermission = useHasDeploymentPermission(
    DeploymentPermissions.canAddConsignment,
  );
  const isMobileView = useHasSelectedMobileView();
  const hasAgencyContext = !isSaleyardAdmin || agencyId !== null;

  const openAttachmentsForAgency = (value: number) => {
    dispatch(setSetting(Settings.agencyId, value));
    openAttachments();
  };

  const baseButton: MultiButtonOption = {
    title: TITLE,
    icon: faFileAlt,
    dataTour: DATA_TOUR,
    isDisabled: !hasCreateConsignmentPermission,
    onClick: null,
  };

  let entry: MultiButtonOption;
  if (hasAgencyContext || isMobileView) {
    entry = { ...baseButton, onClick: openAttachments };
  } else if (agencyOptions.length === 1) {
    entry = {
      ...baseButton,
      onClick: () => openAttachmentsForAgency(agencyOptions[0].value),
    };
  } else {
    entry = {
      ...baseButton,
      subNavItems: agencyOptions.map(agency => ({
        title: agency.label,
        dataTour: `${DATA_TOUR}:${agency.label}`,
        onClick: () => openAttachmentsForAgency(agency.value),
      })),
    };
  }

  return { ...entry, ...overrides };
}
